"use client";
import React from "react";
import { Shield, Mail } from "lucide-react";

export default function PrivacyPolicyPage() {
  const appTitle = process.env.NEXT_PUBLIC_APP_TITLE;
  const contactEmail = process.env.NEXT_PUBLIC_APP_EMAIL;
  const ownerCompany = process.env.NEXT_PUBLIC_APP_COMPANY_NAME;
  const websiteUrl = process.env.NEXT_PUBLIC_APP_COMPANY_URL;

  return (
    <div className="bg-gray-50 lg:py-12">
      <div className="max-w-4xl mx-auto py-7 sm:py-16 px-4 sm:px-6 lg:px-8 bg-white shadow-lg lg:rounded-xl">

        {/* Title and Separator */}
        <div className="mb-8 border-b pb-4 border-gray-200">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 lg:mb-2">
            Privacy Policy
          </h1>
        </div>

        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          {ownerCompany} owns and manages the website, {websiteUrl}. {appTitle} is a platform where offers, deals, and discounts related to food, travel, beauty, lifestyle, etc. are listed either by us or by other sellers.
        </p>

        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          This privacy policy details on how {appTitle} (now referred to as &apos;We&apos;, &apos;Us&apos;, &apos;Our&apos;) collects, use and process the information of the users (now referred to as &apos;you&apos;, &apos;your&apos;, &apos;yourself&apos;). We respect your privacy and are committed to protecting it under all circumstances.        
        </p>

        <p className="text-sm text-gray-600 mb-8 leading-relaxed">
          By visiting the website or accessing some parts of the website, you agree to accept the terms and conditions stated in this Privacy Policy. And, by using this website, you express your consent to our use of your personal information in accordance with this Privacy Policy. If you do not accept the terms and conditions, then do not use or access the website.
        </p>
        {/* --- Section 1: User Information --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          1. User Information
        </h2>

        {/* What We Collect */}
        <div className="bg-primary-50 p-5 rounded-lg mb-6 border border-primary-100">
          <h3 className="text-xl font-bold text-primary-800 mb-3 flex items-center">
            <Shield className="w-5 h-5 mr-2" />
            What Personal Information Do We Collect?
          </h3>
          <p className="text-base text-gray-700 mb-6">
            When you visit and use our website, we may collect the following information about you:
          </p>
          <ul className="list-disc pl-6 text-base text-gray-700 space-y-2">
            <li>Your Email ID and {appTitle} Account Password</li>
            <li>Your Contact Number</li>
            <li>Your Location</li>
            <li>Website Browsing Patterns (Cookies)</li>
            <li>Purchase History</li>
            <li>Payment Details (credit/debit card details, billing address, etc.)</li>
            <li>Any other information that you submit while interacting with the website or using one or more of its product or services.</li>
          </ul>
        </div>

        {/* How We Collect It */}
        <h3 className="text-xl font-semibold text-gray-800 mb-3">
          How We Collect It?
        </h3>
        <p className="text-base text-gray-700 mb-6">
          We get access to your personal information when you:
        </p>
        <ul className="list-disc pl-6 text-base text-gray-700 space-y-2 mb-8">
          <li>Register on the website / Create an account</li>
          <li>Subscribe to our newsletter</li>
          <li>Purchase products/services on the site or through it</li>
          <li>Open Our Emails</li>
          <li>Post Comments On The Site</li>
          <li>Link To The Site Through The Social Media Channels</li>
        </ul>

        {/* Why We Collect It */}
        <h3 className="text-xl font-semibold text-gray-800 mb-3">
          Why We Collect It? (Purposes)
        </h3>
        <p className="text-base text-gray-700 mb-6">
          When you visit and use our website, we may collect the following information about you:
        </p>
        <ul className="list-disc pl-6 text-base text-gray-700 space-y-2 mb-8">
          <li>Provide you with a better user experience</li>
          <li>Improve the website</li>
          <li>Process purchases & reservations</li>
          <li>For personalized marketing via email or direct marketing offers</li>
          <li>Analyze your interests and to provide you additional information about us, our sellers and partners</li>
          <li>To respond to your queries</li>
          <li>For analyze marketing and advertising reach and effectiveness</li>
          <li>To read user behavior</li>
          <li>To send you updates, reminders, support and marketing messages, security alerts and information and services requests made by you among others</li>
          <li>For promotional activities, events, surveys and rewards</li>
          <li>To understand your interests better so that we can provide you with relevant information and offers</li>
          <li>For website analytics</li>
          <li>For legal obligations and to resolve disputes</li>
        </ul>

        {/* Disclosing the information */}
        <h3 className="text-xl font-semibold text-gray-800 mb-3">
          Disclosing the Information
        </h3>
        <p className="text-base text-gray-700 mb-6">
          We understand the importance of securing your personal information. We are committed to securing your personal data and will not share it except in these following cases:-        </p>
        <ul className="list-disc pl-6 text-base text-gray-700 space-y-2 mb-10">
          <li>With our sellers and partners, so that they can provide you with seamless service and deliver the needful.</li>
          <li>For legal compliance or in case of government requests.</li>
          <li>For identity verification and to detect, prevent or investigate frauds, crimes or cyber incidents.</li>
        </ul>
        <div className="text-sm text-red-700 bg-red-50 p-4 rounded-lg mb-8 border border-red-100">
          We try and ensure that our business partners with whom we share the data, comply with our privacy policy and use the data for only the aforementioned purposes. However, their use of the personal data and its processing is governed by their Privacy Policy only and is out of the subject of our control.        </div>


        {/* --- Section 2: Information Security --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          2. Information Security
        </h2>
        <p className="text-base text-gray-700 mb-5">
          We take stringent measures to ensure the privacy, integrity and security of your personal data. We constantly strive to protect your information against any fraudulent or unauthorized usage, access, or disclosure.
        </p>
        <p className="text-base text-gray-700 mb-5">
          For the same, we use the most secure server for systems where your personal data is collected and stored to prevent any unwarranted access. We adopt the highest security measures for data collection, storage and processing.
        </p>
        <p className="text-base text-gray-700 mb-10">
          However, we shall not be responsible for any security breach due to any actions of Third Parties that have your personal information. There are many third party links on the website and we have no control over their privacy policy and security practices.
        </p>


        {/* --- Section 3: Third-Party Advertising --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          3. Third-Party Advertising
        </h2>
        <p className="text-base text-gray-700 mb-10">
          We may employ third-party advertising companies and/or ad agencies to serve ads when you visit our website. These companies may use information (excluding your name, email address, or phone number) about your visits to this website, and your interaction with our products/services, in order to offer advertisements on this website and other third-party websites about products and services that may be of interest to you. They may also use information about your visits to other websites to offer targeted advertisements for products and services. No personally identifiable information is collected in this process.        </p>

        {/* --- Section 4: Links to Other Websites --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          4. Links to Other Websites
        </h2>
        <p className="text-base text-gray-700 mb-10">
          There might be affiliates or other third-party websites linked to {appTitle}. Personal information that you provide to those sites is not our property. These affiliated sites have their separate and independent privacy policies and we encourage you to read their privacy policies when you visit them. {appTitle} does not hold any responsibility or liability for the activities and the content of these third-party sites.        </p>

        {/* --- Section 5: No Warranties --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          5. No Warranties & Disclaimer
        </h2>
        <p className="text-base text-gray-700 mb-6">
          Although {appTitle} attempts to provide accurate content on the website, it makes no warranty that such content is accurate or suitable for any particular purpose or will meet your requirements or will be available on an uninterrupted, timely, secure, or error-free basis.        </p>
        <p className="text-base text-gray-700 mb-10">
          The information contained on our website has been obtained from sources believed to be reliable. {appTitle} disclaims all warranties as to the accuracy, completeness of the materials or the reliability of any advice, statement, opinion or other information distributed through the website. You agree that any reliance on any such statement, opinion, advice or information shall be at your sole risk and shall not be held against {appTitle} for any legal or personal purposes. {appTitle} reserves the right, in its sole discretion, to correct any errors or omissions in any portion of the Website. {appTitle} may change or discontinue any aspect of its website at any time, including, its content described in the website without notice.        </p>

        {/* --- Section 6: Spam and Sale of Information --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          6. Spam and Sale of Information
        </h2>
        <p className="text-base font-bold text-gray-700 mb-10">
          We despise spam. We will never do something that we hate ourselves. Thus, you can rest assured that we will not spam your mailboxes with unsolicited emails, and we will never sell your information to other parties.        </p>

        {/* --- Section 7: Limitation of Liability --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          7. Limitation of Liability
        </h2>
        <p className="text-base text-gray-700 mb-10">
          In no event will {appTitle} be liable for any damages, including without limitation, incidental, special, consequential or punitive damages, whether under a contract, tort or any other theory of liability, arising in connection with any party’s use of the website or in connection with any failure of performance, error, omission, interruption, defect, delay in operation or transmission, computer virus, line system failure, loss of data, or loss of use related to this website or any website operated by any third-party or any contents of this website or any other website, even if {appTitle} is aware of the possibility of such damages.        </p>

        {/* --- Section 8: Indemnification --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          8. Indemnification
        </h2>
        <p className="text-base text-gray-700 mb-10">
          You agree to indemnify, defend and hold {appTitle} harmless from and against all expenses, claims, losses, damages and costs, including reasonable attorney&apos;s fees, arising out of or relating to any misuse by the User of the content and services provided on the website.        
        </p>

        {/* --- Section 9: Changes to this Privacy Policy --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          9. Changes to This Privacy Policy
        </h2>
        <p className="text-base text-gray-700 mb-6">
          We aim to meet the highest of standards. We reserve the right to amend the terms of service mentioned in this Privacy Policy with or without prior notice. However, if you do not agree to the changes, you may discontinue accessing our website. Hence, check this page at regular intervals for the latest changes and updates.        </p>


        {/* Contact CTA */}
        <div className="text-base sm:text-lg text-gray-700 mt-10 pt-4 border-t border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <Mail className="w-5 h-5 mr-2 text-primary-600" />
            10. Contact Us
          </h2>
          <p className="mb-2">
            If you have any queries regarding our privacy policy or any questions about our practices, you can contact us at         
            </p>
          <a href={`mailto:${contactEmail}`} className="text-primary-600 font-semibold hover:text-primary-700 underline transition-colors">
            {contactEmail}
          </a>
        </div>
      </div>
    </div>
  );
}